<template>
    <div id="seating-plan-topbar" class="table-plan rooms bg-light seating-plan-topbar">
        <div class="w-100 d-flex justify-content-between seating-plan-topbar__bar">
            <div class="seating-plan-topbar__left">
                <ul class="nav nav-tabs seating-plan-topbar__rooms">
                    <draggable
                        v-model="sortedRooms"
                        :disabled="!sortable || !has_right_to_update_seatingplan"
                        class="seating-plan-topbar__rooms-track">
                        <li
                            v-for="room in visibleRooms"
                            :key="roomKey(room)"
                            class="seating-plan-topbar__room-shell"
                            :class="{ active: isCurrentRoom(room), 'list-rooms-view': readOnly, 'is-sortable': sortable }">
                            <button type="button" class="btn p-0 seating-plan-topbar__room" @click="$emit('room-selected', room.baseIndex)">
                                <svg
                                    v-if="sortable"
                                    viewBox="0 0 24 24"
                                    width="24"
                                    height="24"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="css-i6dzq1 feather seating-plan-topbar__drag-handle">
                                    <line x1="3" y1="9" x2="21" y2="9"></line>
                                    <line x1="3" y1="15" x2="21" y2="15"></line>
                                </svg>
                                <span class="seating-plan-topbar__room-label">{{ room.name }}</span>
                                <template v-if="readOnly">
                                    <feather
                                        v-if="room.enabled && !room.isEnabledForService"
                                        type="minus-circle"
                                        class="text-danger"
                                        v-tooltip="getTooltip($tl('labels.booking.seatingPlan.room.disabledForService', restaurant_id))" />
                                    <feather
                                        v-else-if="!room.enabled && !room.isEnabledForService"
                                        type="minus-circle"
                                        class="text-danger"
                                        v-tooltip="getTooltip($t('labels.booking.seatingPlan.room.disabledInConfig'))" />
                                    <feather
                                        v-else-if="!room.enabled && room.isEnabledForService"
                                        type="info"
                                        class="text-warning"
                                        v-tooltip="getTooltip($tl('labels.booking.seatingPlan.room.enabledForService', restaurant_id))" />
                                </template>
                            </button>
                            <button
                                v-if="!readOnly && has_right_to_update_seatingplan"
                                type="button"
                                class="delete seating-plan-topbar__room-delete"
                                @click.stop="confirmDelete(room.baseIndex)"></button>
                        </li>
                    </draggable>
                    <li
                        v-if="!readOnly && has_right_to_update_seatingplan"
                        class="nav-item seating-plan-topbar__room-shell"
                        v-tooltip="getTooltip($t('labels.booking.seatingPlan.room.add'))">
                        <button type="button" class="btn p-0 seating-plan-topbar__room seating-plan-topbar__room--add" @click="$emit('new-room')">
                            <feather type="plus" />
                        </button>
                    </li>
                </ul>
                <feather
                    v-if="!readOnly"
                    v-tooltip="getTooltipNotice('notices.booking.tablePlan.rooms')"
                    type="info"
                    class="pointer tooltip-infos seating-plan-topbar__info" />
            </div>
            <div v-if="!readOnly && has_right_to_update_seatingplan" class="align-self-center seating-plan-topbar__actions">
                <div
                    v-if="currentRoom"
                    class="d-inline-flex align-items-center lead-switch seating-plan-topbar__toggle"
                    v-tooltip="getTooltip($t('labels.booking.seatingPlan.room.toggleEnabled'))">
                    <feather
                        v-tooltip="getTooltipNoticeLexicon('notices.booking.tablePlan.disablingRoom', restaurant_id)"
                        type="info"
                        class="me-1 pointer tooltip-infos" />
                    <input type="checkbox" class="switch align-self-center is-rounded" :checked="currentRoom.enabled" />
                    <label class="form-label" @click="toggleCurrentRoomEnabled"></label>
                </div>
                <button type="button" @click="$emit('save')" class="btn btn-success seating-plan-topbar__action seating-plan-topbar__action--save">
                    {{ $tl("labels.form.actions.save") }}
                </button>
            </div>
            <div v-if="readOnly" class="align-self-center seating-plan-topbar__actions seating-plan-topbar__actions--readonly">
                <div v-if="!$_.isEmpty(selectedTables)" class="seating-plan-topbar__readonly-tools">
                    <button
                        v-if="canAddPassingCustomer"
                        type="button"
                        class="btn btn-success btn-circle btn-sm me-1 seating-plan-topbar__action seating-plan-topbar__action--passing"
                        @click="$emit('add-passing-customer-on-tables')">
                        <feather type="plus" /> {{ $tl("labels.booking.reservations.passingCustomer.add.btn") }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-success btn-circle btn-sm me-1 seating-plan-topbar__action seating-plan-topbar__action--reservation"
                        @click="$emit('add-reservation-on-tables')">
                        <feather type="plus" /> {{ $tl("labels.reservation") }}
                    </button>
                    <label v-if="selectedTables.length === 1" class="form-label mb-0 w-auto container-box seating-plan-topbar__check">
                        <input type="checkbox" value="" :checked="selectedTables[0].isEnabledForService" @change="onSelectedTableEnabledChange" />
                        <span class="checkmark"></span>{{ $tl("labels.booking.seatingPlan.table.enable") }}
                    </label>
                </div>
                <div v-else-if="currentRoom">
                    <label class="form-label mb-0 w-auto container-box seating-plan-topbar__check">
                        <input type="checkbox" :checked="currentRoom.isEnabledForService" @change="onSelectedRoomEnabledChange" />
                        <span class="checkmark"></span>{{ $tl("labels.booking.seatingPlan.room.enable") }}
                    </label>
                </div>
            </div>
        </div>
        <confirmation-modal
            v-if="showConfirmationModal"
            :eventPayload="confirmationModalPayload"
            :content="confirmationModalContent"
            @confirmed="confirmationModalCallback"
            @close="showConfirmationModal = false" />
    </div>
</template>

<script>
import ConfirmationModal from "../Modals/messages/ConfirmationModal.vue";
import draggable from "vuedraggable";

export default {
    name: "SeatingPlanTopbar",
    components: {
        ConfirmationModal,
        draggable,
    },
    props: {
        rooms: {
            type: Array,
            default: () => [],
        },
        currentRoom: {
            type: Object,
            default: () => {},
        },
        readOnly: {
            default: false,
            type: Boolean,
        },
        selectedTables: {
            default: null,
        },
        sortable: {
            default: false,
        },
        canAddPassingCustomer: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showConfirmationModal: false,
            confirmationModalPayload: null,
            confirmationModalContent: "",
            confirmationModalCallback: () => {},
        };
    },
    computed: {
        restaurant_id() {
            return Number.parseInt(this.$route.params.restaurant_id);
        },
        rights: function () {
            return this.$store.getters["users/formattedRights"];
        },
        has_right_to_update_seatingplan() {
            return this.rights.includes("booking.seatingplan.update");
        },
        visibleRooms() {
            return this.sortedRooms.filter((room) => !room.closed);
        },
        sortedRooms: {
            get() {
                return this.rooms
                    .map((room, index) => ({
                        ...room,
                        baseIndex: index,
                    }))
                    .sort((leftRoom, rightRoom) => {
                        if (leftRoom.order === rightRoom.order) {
                            return leftRoom.baseIndex - rightRoom.baseIndex;
                        }

                        return leftRoom.order < rightRoom.order ? -1 : 1;
                    });
            },
            set(value) {
                value.forEach((room, index) => {
                    const foundIndex = this.rooms.findIndex((existingRoom) => existingRoom.id === room.id);

                    if (foundIndex > -1) {
                        this.$emit("room-changed", {
                            index: foundIndex,
                            value: {
                                ...this.rooms[foundIndex],
                                order: Number.parseInt(index, 10),
                            },
                        });
                    }
                });
            },
        },
    },
    methods: {
        roomKey(room) {
            return room.id ?? `room-${room.baseIndex}-${room.order}`;
        },
        isCurrentRoom(room) {
            if (!this.currentRoom) {
                return false;
            }

            if (this.currentRoom.id !== undefined && room.id !== undefined) {
                return this.currentRoom.id === room.id;
            }

            return this.rooms[room.baseIndex] === this.currentRoom;
        },
        confirmDelete(index) {
            this.confirmationModalContent = `<p class="text-center">
                    Êtes-vous sûr(e) de vouloir supprimer cette salle ?<br/>
                    <small class="text-muted text-center">
                        Pensez à enregistrer votre plan de salle pour que la suppression soit prise en compte
                    </small>
                </p>`;
            this.confirmationModalPayload = index;
            this.confirmationModalCallback = (indexPayload) => {
                this.showConfirmationModal = false;
                this.$emit("room-deleted", indexPayload);
            };
            this.showConfirmationModal = true;
        },
        onSelectedTableEnabledChange(event) {
            if (event.target.checked) {
                this.$emit("enable-selected-table");
            } else {
                this.$emit("disable-selected-table");
            }
        },
        onSelectedRoomEnabledChange(event) {
            this.$emit("change-room-enabled", event.target.checked);
        },
        toggleCurrentRoomEnabled() {
            if (!this.currentRoom) {
                return;
            }

            this.$emit("change-room-enabled", !this.currentRoom.enabled);
        },
    },
};
</script>

<style scoped>
.seating-plan-topbar,
.seating-plan-topbar__bar,
.seating-plan-topbar__left {
    min-width: 0;
}

.seating-plan-topbar__left,
.seating-plan-topbar__rooms,
.seating-plan-topbar__rooms-track,
.seating-plan-topbar__room-shell,
.seating-plan-topbar__actions,
.seating-plan-topbar__readonly-tools {
    align-items: center;
    display: flex;
}

.seating-plan-topbar__rooms {
    flex-wrap: nowrap;
    margin: 0;
    overflow-x: auto;
    padding: 0;
}

.seating-plan-topbar__rooms-track {
    flex: 0 0 auto;
}

.seating-plan-topbar__room-shell {
    flex: 0 0 auto;
    position: relative;
}

.seating-plan-topbar__room {
    color: inherit;
    font-size: inherit;
    line-height: inherit;
    white-space: nowrap;
}

.seating-plan-topbar__room-delete {
    margin-left: 0.35rem;
}

.seating-plan-topbar__actions {
    flex: 0 0 auto;
    white-space: nowrap;
}
</style>
